/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createInstance, Identify } from '@amplitude/analytics-browser';
import type { AmplitudeBrowser } from '@amplitude/analytics-browser/lib/scripts/browser-client';
import { isNewSession } from '@amplitude/analytics-client-common';
import type { Context } from '@nuxt/types';
import ahoy from 'ahoy.js';
import { defineNuxtPlugin } from '@nuxtjs/composition-api';
import { linkedinConversions } from './3rd-party/linkedin/conversion_ids';
import { detectChannel } from '~/components/utils/traffic_channel';
import { transformObjectKeys } from '~/components/utils/core/object';

const AMPLITUDE_ID_DEVELOPMENT = '0ba7dc92c79c9645cef2dbeed059b4bd';
const AMPLITUDE_ID_PRODUCTION = 'e508b97e32f23f43e3fb1c9087d5b7d8';
const currentABTestName: string = '';
const amplitude = createInstance() as AmplitudeBrowser;
const amplitudeDefaultSessionTimeout = 30 * 60 * 1000;

const AMPLITUDE_ID = process.env.isProduction ? AMPLITUDE_ID_PRODUCTION : AMPLITUDE_ID_DEVELOPMENT;
const { b2cSignUp } = linkedinConversions;

const getProps = (ctx: Context, currentProps: any) => {
  const props = {
    ...currentProps,
    route_name: ctx.route.name,
    pathname: ctx.route.path,
    from_web: true,
  };

  const ab_test = ctx.store.state.auth.loggedIn && JSON.parse(JSON.stringify(ctx.store.state.auth.user.abTests));

  if (ab_test) {
    props.ab_test = transformObjectKeys((key: string) => key.replace(/^ab-test-/, ''))(ab_test);
  }

  Object.keys(ctx.$cookies?.getAll()).forEach(cookie => {
    if (cookie.match(/^ab-test-/)) {
      if (!props.ab_test) props.ab_test = {};
      props.ab_test[cookie.replace(/^ab-test-/, '')] = ctx.$cookies?.get(cookie);
    }
  });

  return props;
};

const AHOY_CONFIG = {
  visitsUrl: `/bucket/apples`,
  eventsUrl: `/bucket/oranges`,
  visitParams: {
    traffic_channel: detectChannel(),
  },
  visitDuration: 30,
};

ahoy.configure(AHOY_CONFIG);

export default defineNuxtPlugin((ctx, inject) => {
  const isAdmin = ctx.store.state.auth.user?.admin || ctx.store.state.auth.user?.isImpersonateMode;
  const analyticsEnabled = process.env.NUXT_ANALYTICAL_TRACKING === 'true' && (!isAdmin || !process.env.isProduction);

  function setAmplitudeUserProperties(props: Record<string, unknown>) {
    if (analyticsEnabled) {
      const identify = new Identify();
      for (const [key, value] of Object.entries(props || {})) {
        identify.set(key, value as string);
      }
      amplitude.identify(identify);
    }
  }

  if (analyticsEnabled) {
    const userId = ctx.store.state.auth.loggedIn ? ctx.store.state.auth.user.id : null;
    amplitude.init(AMPLITUDE_ID, userId, {
      minIdLength: 2,
      defaultTracking: {
        attribution: true,
        pageViews: false,
        formInteractions: false,
      },
    });
    if (isNewSession(amplitudeDefaultSessionTimeout)) {
      setAmplitudeUserProperties({ traffic_channel: detectChannel() });
    }
  }

  ctx.$ga.set('dimension1', ctx.store.state.auth.loggedIn);

  if (ctx.store.state.auth.loggedIn && ctx.store.state.shouldSyncAmplitudeUserProperties) {
    if (!currentABTestName) return;

    const userABTestPropName = currentABTestName.replace(/^ab-test-(\d)/, 'ab_user__$1');

    setAmplitudeUserProperties({ [userABTestPropName]: ctx.store.state.auth.user.abTests[currentABTestName] });
  }

  let metadata = {};
  const setMetadata = (newMetaData: never) => (metadata = newMetaData);
  const clearMetaData = () => (metadata = {});

  function logAdsEvent(name: string, props: Record<string, unknown>) {
    const eventsMap = {
      'global__user__sign-up': 'Subscribe',
      'global__skwSlug__email-apply': 'Lead',
      onboarding__finish: 'CompleteRegistration',
    };

    if ((eventsMap as never)[name]) {
      (eventsMap as never)[name] === 'Lead'
        ? ctx.$3rdParty.tiktok.track('SubmitForm', props)
        : ctx.$3rdParty.tiktok.track((eventsMap as never)[name], props);

      ctx.$3rdParty.facebook('track', (eventsMap as never)[name], { ...props, email: ctx.store.state.auth.user.email });
    }
  }

  function logEvent(name: string, props: Record<string, unknown>, cb?: (...args: any[]) => unknown) {
    const newProps = getProps(ctx, props);

    console.info('Event:', name, newProps, cb);
    ahoy.track(name, newProps);

    if (analyticsEnabled) {
      if (isNewSession(amplitudeDefaultSessionTimeout)) {
        setAmplitudeUserProperties({ traffic_channel: detectChannel() });
      }
      amplitude.track(name, newProps).promise.then(cb);

      logAdsEvent(name, newProps);
    }
  }

  const logAhoyEvent = (name: string, props: Record<string, unknown>) => {
    const newProps = getProps(ctx, props);

    ahoy.track(name, newProps);
  };

  function logSignUp(
    type: unknown,
    additionalData: Record<string, unknown> = {},
    amplitudeUser: Record<string, unknown>,
  ) {
    if (analyticsEnabled) {
      try {
        setAmplitudeUserProperties(amplitudeUser);
      } catch (e) {
        ctx.app.$sentryUtils.error(e as Error, "[analytics]: pixel hasn't loaded for sign up event");
      }
    }
    process.env.isProduction && ctx.$gtag.event('conversion', { send_to: 'AW-10888375321/4eRvCM2NxrgDEJnY_cco' });
    ctx.$3rdParty.linkedin('track', { conversion_id: b2cSignUp });

    ctx.$gtag.event('Goal 2 - B2C Sign Up');

    logEvent('global__user__sign-up', { type, ...additionalData });
  }

  function logPageView({ name, path, ...rest }: Record<string, unknown> = {}) {
    const params = {
      from_route_name: ctx.from?.name,
      route_name: name,
      pathname: path,
      location: window.location.href,
      ...rest,
      ...metadata,
    };
    logEvent('global__page__view', params);
    clearMetaData();
  }

  function setUser(user: Record<string, any>, oldUser: Record<string, any>) {
    const userId = (user && user.id) || null;
    const oldUserId = (oldUser && oldUser.id) || null;

    ctx.$ga.set('dimension1', !!userId);

    if (analyticsEnabled && userId) {
      amplitude.setUserId(userId);
    }

    if (oldUserId !== userId && !userId) {
      amplitude.reset();
    }
  }

  // Send all events before hide/close
  // https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/migration/#flush-or-onexitpage
  if (process.client) {
    window.addEventListener('pagehide', () => {
      amplitude.setTransport('beacon');
      amplitude.flush();
    });
  }

  ctx.app.store?.watch(store => store.auth.user, setUser, { immediate: true });

  inject('analytics', {
    logEvent,
    logAhoyEvent,
    logSignUp,
    logPageView,
    setUser,
    setAmplitudeUserProperties,
    setMetadata,
    clearMetaData,
  });
});
